import React, { useState } from 'react';

const LeadDetail = ({ lead, onBack, onExport }) => {
  const [activeTab, setActiveTab] = useState('basic');

  const tabs = [
    { id: 'basic', label: 'Basic Information', icon: '🏢' },
    { id: 'metrics', label: 'Installation Metrics', icon: '📊' },
    { id: 'financial', label: 'Financial Data', icon: '💰' },
    { id: 'geographic', label: 'Geographic Coverage', icon: '🗺️' },
    { id: 'equipment', label: 'Equipment & Technology', icon: '⚡' },
    { id: 'compliance', label: 'Risk & Compliance', icon: '🛡️' }
  ];

  const formatValue = (value, key) => {
    if (value === null || value === undefined) return '-';
    
    switch (key) {
      case 'total_installations':
      case 'installations_last_30_days':
      case 'installations_last_90_days':
      case 'installations_last_365_days':
        return value.toLocaleString();
      case 'avg_system_size_kw':
        return `${value.toFixed(1)} kW`;
      case 'avg_cost_per_watt':
        return `$${value.toFixed(2)}`;
      case 'risk_score':
        return value.toFixed(1);
      case 'estimated_annual_revenue':
        return value >= 1000000 ? `$${(value / 1000000).toFixed(1)}M` : `$${(value / 1000).toFixed(0)}K`;
      case 'approval_rate':
      case 'decommission_rate':
        return `${(value * 100).toFixed(1)}%`;
      case 'residential_pct':
      case 'commercial_pct':
      case 'utility_pct':
        return `${(value * 100).toFixed(1)}%`;
      default:
        return value;
    }
  };

  const getRiskScoreColor = (score) => {
    if (score <= 3) return '#22C55E'; // Green
    if (score <= 6) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  const styles = {
    container: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '16px',
      overflow: 'hidden',
      backdropFilter: 'blur(10px)',
      animation: 'slideInRight 0.5s ease forwards'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '24px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(255, 255, 255, 0.02)'
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    backButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      background: 'rgba(255, 255, 255, 0.1)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.15)',
        transform: 'translateX(-2px)'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    title: {
      fontSize: '24px',
      fontWeight: '700',
      color: '#ffffff',
      margin: 0
    },
    subtitle: {
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.7)',
      margin: '4px 0 0 0'
    },
    headerRight: {
      display: 'flex',
      gap: '12px'
    },
    actionButton: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
      border: 'none',
      borderRadius: '8px',
      color: '#ffffff',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: '0 8px 25px rgba(59, 130, 246, 0.3)'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    tabs: {
      display: 'flex',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(255, 255, 255, 0.02)'
    },
    tab: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '16px 24px',
      background: 'transparent',
      border: 'none',
      color: 'rgba(255, 255, 255, 0.7)',
      fontSize: '14px',
      fontWeight: '500',
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      borderBottom: '2px solid transparent',
      position: 'relative',
      '&:hover': {
        color: 'rgba(255, 255, 255, 0.9)',
        background: 'rgba(255, 255, 255, 0.05)'
      },
      '&:focus': {
        outline: '2px solid #3B82F6',
        outlineOffset: '2px'
      }
    },
    tabActive: {
      color: '#3B82F6',
      borderBottomColor: '#3B82F6',
      background: 'rgba(59, 130, 246, 0.1)',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-2px',
        left: '0',
        right: '0',
        height: '2px',
        background: '#3B82F6',
        animation: 'slideInLeft 0.3s ease forwards'
      }
    },
    content: {
      padding: '24px',
      animation: 'fadeInUp 0.5s ease forwards',
      animationDelay: '0.2s'
    },
    section: {
      marginBottom: '32px',
      animation: 'fadeInUp 0.5s ease forwards',
      animationDelay: '0.3s'
    },
    sectionTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#ffffff',
      marginBottom: '16px',
      paddingBottom: '8px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      position: 'relative',
      '&::after': {
        content: '""',
        position: 'absolute',
        bottom: '-1px',
        left: '0',
        width: '50px',
        height: '2px',
        background: 'linear-gradient(90deg, #3B82F6, #2563EB)',
        animation: 'slideInLeft 0.5s ease forwards'
      }
    },
    grid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '20px',
      animation: 'fadeInUp 0.5s ease forwards',
      animationDelay: '0.4s'
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
      gap: '4px',
      transition: 'all 0.2s ease',
      '&:hover': {
        transform: 'translateY(-2px)'
      }
    },
    fieldLabel: {
      fontSize: '12px',
      fontWeight: '500',
      color: 'rgba(255, 255, 255, 0.6)',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    fieldValue: {
      fontSize: '14px',
      color: '#ffffff',
      fontWeight: '500'
    },
    metricCard: {
      background: 'rgba(255, 255, 255, 0.05)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      borderRadius: '12px',
      padding: '20px',
      textAlign: 'center',
      transition: 'all 0.3s ease',
      '&:hover': {
        background: 'rgba(255, 255, 255, 0.08)',
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)'
      }
    },
    metricValue: {
      fontSize: '32px',
      fontWeight: '700',
      color: '#3B82F6',
      marginBottom: '8px'
    },
    metricLabel: {
      fontSize: '14px',
      color: 'rgba(255, 255, 255, 0.7)',
      fontWeight: '500'
    },
    progressBar: {
      width: '100%',
      height: '8px',
      background: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '4px',
      overflow: 'hidden',
      marginTop: '8px'
    },
    progressFill: {
      height: '100%',
      background: 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)',
      borderRadius: '4px',
      transition: 'width 0.3s ease'
    },
    riskScore: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 16px',
      borderRadius: '20px',
      fontSize: '16px',
      fontWeight: '600'
    },
    tierBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '6px 12px',
      borderRadius: '8px',
      fontSize: '12px',
      fontWeight: '600',
      textTransform: 'uppercase',
      letterSpacing: '0.5px'
    },
    contactInfo: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '8px 12px',
      background: 'rgba(255, 255, 255, 0.05)',
      borderRadius: '8px',
      fontSize: '14px',
      color: '#ffffff'
    },
    contactButton: {
      padding: '4px 8px',
      background: 'rgba(59, 130, 246, 0.2)',
      border: '1px solid rgba(59, 130, 246, 0.3)',
      borderRadius: '4px',
      color: '#3B82F6',
      fontSize: '12px',
      cursor: 'pointer',
      transition: 'all 0.2s ease'
    }
  };

  const getTierBadgeColor = (tier) => {
    switch (tier) {
      case 'FREE':
        return { bg: 'rgba(34, 197, 94, 0.2)', text: '#22C55E', border: 'rgba(34, 197, 94, 0.3)' };
      case 'STARTER':
        return { bg: 'rgba(59, 130, 246, 0.2)', text: '#3B82F6', border: 'rgba(59, 130, 246, 0.3)' };
      case 'PRO':
        return { bg: 'rgba(168, 85, 247, 0.2)', text: '#A855F7', border: 'rgba(168, 85, 247, 0.3)' };
      default:
        return { bg: 'rgba(107, 114, 128, 0.2)', text: '#6B7280', border: 'rgba(107, 114, 128, 0.3)' };
    }
  };

  const renderBasicInfo = () => (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Company Information</h3>
      <div style={styles.grid}>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Primary Business Name</span>
          <span style={styles.fieldValue}>{lead.primary_business_name || '-'}</span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>License Number</span>
          <span style={styles.fieldValue}>{lead.license_number || '-'}</span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>All Business Names</span>
          <span style={styles.fieldValue}>{lead.all_business_names || '-'}</span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Access Tier</span>
          <span
            style={{
              ...styles.tierBadge,
              ...getTierBadgeColor(lead.access_tier)
            }}
          >
            {lead.access_tier}
          </span>
        </div>
      </div>

      <h3 style={styles.sectionTitle}>Contact Information</h3>
      <div style={styles.grid}>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Phone Numbers</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {lead.all_phones ? (
              lead.all_phones.split(',').map((phone, index) => (
                <div key={index} style={styles.contactInfo}>
                  <span>📞 {phone.trim()}</span>
                  <button style={styles.contactButton}>Call</button>
                </div>
              ))
            ) : (
              <span style={styles.fieldValue}>-</span>
            )}
          </div>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Addresses</span>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {lead.all_addresses ? (
              lead.all_addresses.split(',').map((address, index) => (
                <div key={index} style={styles.contactInfo}>
                  <span>📍 {address.trim()}</span>
                </div>
              ))
            ) : (
              <span style={styles.fieldValue}>-</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderMetrics = () => (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Installation Overview</h3>
      <div style={styles.grid}>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{formatValue(lead.total_installations, 'total_installations')}</div>
          <div style={styles.metricLabel}>Total Installations</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{formatValue(lead.avg_system_size_kw, 'avg_system_size_kw')}</div>
          <div style={styles.metricLabel}>Average System Size</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{formatValue(lead.avg_cost_per_watt, 'avg_cost_per_watt')}</div>
          <div style={styles.metricLabel}>Average Cost per Watt</div>
        </div>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{formatValue(lead.avg_days_to_approval, 'avg_days_to_approval')}</div>
          <div style={styles.metricLabel}>Avg Days to Approval</div>
        </div>
      </div>

      <h3 style={styles.sectionTitle}>Recent Activity</h3>
      <div style={styles.grid}>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Last 30 Days</span>
          <span style={styles.fieldValue}>{formatValue(lead.installations_last_30_days, 'installations_last_30_days')}</span>
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: `${Math.min((lead.installations_last_30_days / Math.max(lead.total_installations, 1)) * 100, 100)}%`
              }}
            />
          </div>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Last 90 Days</span>
          <span style={styles.fieldValue}>{formatValue(lead.installations_last_90_days, 'installations_last_90_days')}</span>
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: `${Math.min((lead.installations_last_90_days / Math.max(lead.total_installations, 1)) * 100, 100)}%`
              }}
            />
          </div>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Last 365 Days</span>
          <span style={styles.fieldValue}>{formatValue(lead.installations_last_365_days, 'installations_last_365_days')}</span>
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: `${Math.min((lead.installations_last_365_days / Math.max(lead.total_installations, 1)) * 100, 100)}%`
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderFinancial = () => (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Financial Performance</h3>
      <div style={styles.grid}>
        <div style={styles.metricCard}>
          <div style={styles.metricValue}>{formatValue(lead.estimated_annual_revenue, 'estimated_annual_revenue')}</div>
          <div style={styles.metricLabel}>Estimated Annual Revenue</div>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Approval Rate</span>
          <span style={styles.fieldValue}>{formatValue(lead.approval_rate, 'approval_rate')}</span>
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: `${(lead.approval_rate || 0) * 100}%`
              }}
            />
          </div>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Decommission Rate</span>
          <span style={styles.fieldValue}>{formatValue(lead.decommission_rate, 'decommission_rate')}</span>
          <div style={styles.progressBar}>
            <div 
              style={{
                ...styles.progressFill,
                width: `${(lead.decommission_rate || 0) * 100}%`
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );

  const renderGeographic = () => (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Service Areas</h3>
      <div style={styles.grid}>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Primary Service City</span>
          <span style={styles.fieldValue}>{lead.primary_service_city || '-'}</span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>All Cities</span>
          <span style={styles.fieldValue}>{lead.all_cities || '-'}</span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>All ZIP Codes</span>
          <span style={styles.fieldValue}>{lead.all_zips || '-'}</span>
        </div>
      </div>
    </div>
  );

  const renderEquipment = () => (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Equipment & Technology</h3>
      <div style={styles.grid}>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Primary Panel Brand</span>
          <span style={styles.fieldValue}>{lead.primary_panel_brand || '-'}</span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Primary Inverter Brand</span>
          <span style={styles.fieldValue}>{lead.primary_inverter_brand || '-'}</span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Residential Percentage</span>
          <span style={styles.fieldValue}>{formatValue(lead.residential_pct, 'residential_pct')}</span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Commercial Percentage</span>
          <span style={styles.fieldValue}>{formatValue(lead.commercial_pct, 'commercial_pct')}</span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Utility Percentage</span>
          <span style={styles.fieldValue}>{formatValue(lead.utility_pct, 'utility_pct')}</span>
        </div>
      </div>
    </div>
  );

  const renderCompliance = () => (
    <div style={styles.section}>
      <h3 style={styles.sectionTitle}>Risk & Compliance</h3>
      <div style={styles.grid}>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Risk Score</span>
          <span
            style={{
              ...styles.riskScore,
              background: `rgba(${getRiskScoreColor(lead.risk_score).replace('#', '0x')}, 0.2)`,
              color: getRiskScoreColor(lead.risk_score)
            }}
          >
            {formatValue(lead.risk_score, 'risk_score')}
          </span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Active Installer Flag</span>
          <span style={styles.fieldValue}>
            {lead.active_installer_flag ? '✅ Active' : '❌ Inactive'}
          </span>
        </div>
        <div style={styles.field}>
          <span style={styles.fieldLabel}>Last Updated</span>
          <span style={styles.fieldValue}>
            {lead.last_updated_at ? new Date(lead.last_updated_at).toLocaleDateString() : '-'}
          </span>
        </div>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'basic':
        return renderBasicInfo();
      case 'metrics':
        return renderMetrics();
      case 'financial':
        return renderFinancial();
      case 'geographic':
        return renderGeographic();
      case 'equipment':
        return renderEquipment();
      case 'compliance':
        return renderCompliance();
      default:
        return renderBasicInfo();
    }
  };

  if (!lead) {
    return (
      <div style={styles.container}>
        <div style={{ padding: '40px', textAlign: 'center', color: 'rgba(255, 255, 255, 0.7)' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📄</div>
          <h3>No lead selected</h3>
          <p>Please select a lead to view details</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.headerLeft}>
          <button style={styles.backButton} onClick={onBack}>
            ← Back to Leads
          </button>
          <div>
            <h1 style={styles.title}>{lead.primary_business_name}</h1>
            <p style={styles.subtitle}>License: {lead.license_number}</p>
          </div>
        </div>
        <div style={styles.headerRight}>
          <button style={styles.actionButton} onClick={() => onExport('csv', [lead.business_id])}>
            📥 Export Lead
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div style={styles.tabs}>
        {tabs.map(tab => (
          <button
            key={tab.id}
            style={{
              ...styles.tab,
              ...(activeTab === tab.id ? styles.tabActive : {})
            }}
            onClick={() => setActiveTab(tab.id)}
          >
            <span>{tab.icon}</span>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div style={styles.content}>
        {renderTabContent()}
      </div>

      {/* CSS Animations */}
      <style jsx>{`
        @keyframes slideInRight {
          from {
            opacity: 0;
            transform: translateX(50px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes slideInLeft {
          from {
            width: 0;
          }
          to {
            width: 100%;
          }
        }
      `}</style>
    </div>
  );
};

export default LeadDetail; 